/** 行×列の矩形マトリクスに揃える（cells[ri] 欠落時のクラッシュ防止） */
export const normalizeHeatmapMatrix = (rowLabels = [], colLabels = [], cells = []) => {
  const rows = rowLabels.length ? rowLabels : ['—'];
  const cols = colLabels.length ? colLabels : ['—'];
  return rows.map((_, ri) =>
    cols.map((_, ci) => {
      const row = cells?.[ri];
      if (!Array.isArray(row)) return 0;
      const v = row[ci];
      return typeof v === 'number' && !Number.isNaN(v) ? v : 0;
    }),
  );
};

const timeColLabels = (slotLists) => {
  const keys = new Map();
  slotLists.flat().forEach((s) => {
    const k = s?.timeLabel || s?.label;
    if (k) keys.set(k, k);
  });
  return [...keys.values()];
};

const buildRowFromSlots = (colLabels, slots = []) =>
  colLabels.map((col) => {
    const slot = slots.find((s) => (s.timeLabel || s.label) === col);
    return slot?.raw || 0;
  });

/** スロット配列 → TVHeatmapGrid 用マトリクス */
export const slotsToHeatmap = (slots = [], rowLabel = '混雑') => {
  const colLabels = (slots ?? []).map((s) => s.timeLabel || s.label).filter(Boolean);
  const cols = colLabels.length ? colLabels : ['—'];
  const cells = normalizeHeatmapMatrix([rowLabel], cols, [buildRowFromSlots(cols, slots ?? [])]);
  return {
    rowLabels: [rowLabel],
    colLabels: cols,
    cells,
  };
};

export const slotsRowsToHeatmap = (rows) => {
  const colSet = new Map();
  rows.forEach(({ slots }) => {
    slots.forEach((s) => {
      const key = s.timeLabel || s.label;
      colSet.set(key, key);
    });
  });
  const colLabels = [...colSet.keys()];
  const rowLabels = rows.map((r) => r.label);
  const cells = rows.map(({ slots }) =>
    colLabels.map((col) => {
      const slot = slots.find((s) => (s.timeLabel || s.label) === col);
      return slot?.raw || 0;
    }),
  );
  return { rowLabels, colLabels, cells };
};

export const laterDaysToHeatmap = (laterDays) => ({
  rowLabels: ['以降'],
  colLabels: laterDays.map((d) => d.dateLabel),
  cells: [laterDays.map((d) => d.raw || 0)],
});

/** 複数日×時間帯の1枚表 */
export const buildMultiDayHeatmap = (dayRows = []) => {
  const weekRows = dayRows
    .filter((row) => row?.label)
    .map((row) => ({ label: row.label, slots: row.slots ?? [] }));

  const colLabels = timeColLabels(weekRows.map((row) => row.slots));

  if (!colLabels.length) {
    return {
      rowLabels: ['—'],
      colLabels: ['—'],
      cells: [[0]],
      rowHeader: '日',
    };
  }

  const rowLabels = weekRows.map((row) => row.label);
  const rawCells = weekRows.map((row) => buildRowFromSlots(colLabels, row.slots));

  return {
    rowLabels,
    colLabels,
    cells: normalizeHeatmapMatrix(rowLabels, colLabels, rawCells),
    rowHeader: '日',
  };
};

/** 画面3：1枚の表（今日〜先の日を行に並べる） */
export const buildWeekUnifiedHeatmap = ({
  today,
  tomorrow,
  dayAfter,
  laterDays = [],
}) => {
  const weekRows = [
    { label: today?.label ?? '今日', slots: today?.slots ?? [] },
    { label: tomorrow?.label ?? '明日', slots: tomorrow?.slots ?? [] },
    { label: dayAfter?.label ?? '明後日', slots: dayAfter?.slots ?? [] },
    ...(laterDays ?? [])
      .filter((day) => day?.dateLabel)
      .map((day) => ({ label: day.dateLabel, slots: day.slots ?? [] })),
  ];

  return buildMultiDayHeatmap(weekRows);
};
